import React, { useState, useEffect, useRef } from 'react';
import qaService from '../services/qaService';
import LoadingSpinner from './LoadingSpinner';

/**
 * Q&A Chat Modal Component - Enhanced Beautiful UI
 * Provides an interactive chat interface for asking questions about indexed transcriptions
 */
const QAChatModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [indexedCompanies, setIndexedCompanies] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  // Sample questions to help users get started
  const sampleQuestions = [
    "What was the revenue growth in Q1 2024?",
    "What are the key challenges mentioned?",
    "What are the future growth plans?",
    "How did the company perform this quarter?",
    "What are the main highlights from the earnings call?"
  ];

  // Load chat history from sessionStorage on component mount
  useEffect(() => {
    const loadChatHistory = () => {
      try {
        const savedHistory = sessionStorage.getItem('qaChatHistory');
        if (savedHistory) {
          const parsedHistory = JSON.parse(savedHistory);
          setChatHistory(parsedHistory);
        }
      } catch (error) {
        console.error('Failed to load chat history:', error);
      }
    };
    
    loadChatHistory();
  }, []);

  // Save chat history to sessionStorage whenever it changes
  useEffect(() => {
    try {
      if (chatHistory.length > 0) {
        sessionStorage.setItem('qaChatHistory', JSON.stringify(chatHistory));
      }
    } catch (error) {
      console.error('Failed to save chat history:', error);
    }
  }, [chatHistory]);

  useEffect(() => {
    if (isOpen) {
      loadIndexedCompanies();
      // Focus input when modal opens
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  const loadIndexedCompanies = async () => {
    try {
      const response = await qaService.getIndexedCompanies();
      setIndexedCompanies(response.companies || []);
    } catch (error) {
      console.error('Failed to load indexed companies:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim() || isLoading) return;

    const userMessage = {
      type: 'user',
      content: query,
      timestamp: new Date().toISOString()
    };

    // Add user message to chat
    setChatHistory(prev => [...prev, userMessage]);
    setQuery('');
    setIsLoading(true);
    setShowSuggestions(false);

    try {
      const response = await qaService.askQuestion(query);
      
      const botMessage = {
        type: 'bot',
        content: response.answer,
        sources: response.sources || [],
        metadata: response.metadata || {},
        extractedIntent: response.extracted_intent || {},
        timestamp: new Date().toISOString()
      };

      setChatHistory(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        type: 'error',
        content: error.message || 'Failed to get answer. Please try again.',
        timestamp: new Date().toISOString()
      };
      setChatHistory(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSampleQuestion = (question) => {
    setQuery(question);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const clearChat = () => {
    setChatHistory([]);
    setQuery('');
    // Clear chat history from sessionStorage
    try {
      sessionStorage.removeItem('qaChatHistory');
    } catch (error) {
      console.error('Failed to clear chat history:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden animate-fadeIn">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-30 transition-all duration-300"
        onClick={onClose}
      />
      
      {/* Modal with slide-in animation */}
      <div className="absolute right-0 top-16 bottom-0 w-full md:w-2/3 lg:w-1/2 xl:w-2/5 bg-gradient-to-br from-white to-gray-50 shadow-2xl flex flex-col animate-slideInRight">
        {/* Enhanced Header with gradient and glow effect */}
        <div className="relative bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white p-5 flex items-center justify-between shadow-lg overflow-hidden">
          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-gradient-to-br from-white via-transparent to-transparent animate-pulse"></div>
          </div>
          
          <div className="flex items-center space-x-3 relative z-10">
            <div className="w-12 h-12 bg-white bg-opacity-20 backdrop-blur-md rounded-xl flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform duration-200">
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight flex items-center">
                AI Q&A Assistant
                <span className="ml-2 text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full font-medium">BETA</span>
              </h2>
              <p className="text-sm text-blue-100 font-medium">
                {indexedCompanies.length > 0 
                  ? `✨ ${indexedCompanies.length} companies indexed & ready` 
                  : 'Query your earnings call transcriptions'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 relative z-10">
            {chatHistory.length > 0 && (
              <button
                onClick={clearChat}
                className="flex items-center space-x-2 px-4 py-2.5 bg-white bg-opacity-15 hover:bg-opacity-25 backdrop-blur-md rounded-xl transition-all duration-200 transform hover:scale-105 shadow-lg border border-white border-opacity-20"
                title="Clear chat history"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span className="text-sm font-semibold">Clear</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-white hover:bg-opacity-20 rounded-xl transition-all duration-200 transform hover:scale-105"
              title="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Chat Messages with custom scrollbar */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gradient-to-br from-gray-50 to-blue-50 custom-scrollbar">
          {chatHistory.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8 animate-fadeIn">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl flex items-center justify-center mb-6 shadow-2xl transform hover:scale-110 transition-transform duration-300">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-3">
                Ask Me Anything
              </h3>
              <p className="text-gray-600 mb-8 max-w-md leading-relaxed text-lg">
                Search across indexed earnings calls instantly. Get AI-powered answers with source citations.
              </p>
              
              <div className="w-full max-w-lg">
                <p className="text-sm font-semibold text-gray-700 mb-4 flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                  </svg>
                  Try these sample questions
                </p>
                <div className="space-y-3">
                  {sampleQuestions.map((question, index) => (
                    <button
                      key={index}
                      onClick={() => handleSampleQuestion(question)}
                      className="w-full text-left p-4 bg-white hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 border-2 border-gray-200 hover:border-blue-300 rounded-xl text-sm text-gray-700 hover:text-blue-700 transition-all duration-200 transform hover:scale-102 hover:shadow-lg group"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <span className="flex items-center">
                        <span className="text-2xl mr-3 group-hover:scale-110 transition-transform">💡</span>
                        <span className="font-medium">{question}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              {chatHistory.map((message, index) => (
                <div
                  key={index}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} animate-fadeInUp`}
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {message.type === 'user' ? (
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl rounded-tr-sm px-5 py-3 max-w-3/4 shadow-lg transform hover:scale-102 transition-transform duration-200">
                      <p className="text-sm leading-relaxed font-medium">{message.content}</p>
                      <p className="text-xs text-blue-100 mt-1 opacity-75">
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </p>
                    </div>
                  ) : message.type === 'error' ? (
                    <div className="bg-gradient-to-r from-red-50 to-pink-50 text-red-700 rounded-2xl px-5 py-4 max-w-3/4 border-2 border-red-200 shadow-lg">
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <p className="text-sm font-semibold mb-1">Error</p>
                          <p className="text-sm">{message.content}</p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-white rounded-2xl rounded-tl-sm px-6 py-4 max-w-3/4 shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
                      {/* Extracted Intent Badge */}
                      {message.extractedIntent && Object.keys(message.extractedIntent).length > 0 && (
                        <div className="mb-4 flex items-center space-x-2">
                          <div className="flex items-center space-x-2 bg-gradient-to-r from-blue-50 to-indigo-50 px-3 py-1.5 rounded-full border border-blue-200">
                            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                            <span className="text-xs font-semibold text-blue-700">
                              {message.extractedIntent.company_name && (
                                <span className="uppercase">{message.extractedIntent.company_name}</span>
                              )}
                              {message.extractedIntent.quarter && message.extractedIntent.year && (
                                <span> • {message.extractedIntent.quarter} {message.extractedIntent.year}</span>
                              )}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Answer with better typography */}
                      <div className="prose prose-sm max-w-none mb-4">
                        <p className="text-gray-800 whitespace-pre-wrap leading-relaxed text-base font-medium">{message.content}</p>
                      </div>
                      
                      {/* Compact Sources Section */}
                      {message.sources && message.sources.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <details className="group">
                            <summary className="flex items-center justify-between cursor-pointer text-xs text-gray-600 hover:text-blue-600 transition-colors">
                              <span className="flex items-center font-medium">
                                <svg className="w-4 h-4 mr-1.5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M9 4.804A7.968 7.968 0 005.5 4c-1.255 0-2.443.29-3.5.804v10A7.969 7.969 0 015.5 14c1.669 0 3.218.51 4.5 1.385A7.962 7.962 0 0114.5 14c1.255 0 2.443.29 3.5.804v-10A7.968 7.968 0 0014.5 4c-1.255 0-2.443.29-3.5.804V12a1 1 0 11-2 0V4.804z" />
                                </svg>
                                View {message.sources.length} source{message.sources.length > 1 ? 's' : ''}
                              </span>
                              <svg className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </summary>
                            <div className="mt-2 space-y-2">
                              {message.sources.map((source, idx) => (
                                <div 
                                  key={idx} 
                                  className="bg-gray-50 rounded-lg p-2.5 border border-gray-200 hover:border-blue-200 transition-colors"
                                >
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span className="flex items-center space-x-1.5">
                                      <span className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                                        {idx + 1}
                                      </span>
                                      <span className="text-xs text-gray-600">
                                        Chunk {source.chunk_index}
                                      </span>
                                    </span>
                                    {source.relevance_score && (
                                      <span className="flex items-center space-x-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-medium">
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        {(source.relevance_score * 100).toFixed(0)}%
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">
                                    {source.text}
                                  </p>
                                  <div className="flex items-center space-x-1.5 text-xs text-gray-500 mt-1.5">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span>{source.quarter} {source.year}</span>
                                    <span>•</span>
                                    <span>{source.date}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </details>
                        </div>
                      )}
                      
                      {/* Metadata with better styling */}
                      {message.metadata && message.metadata.response_time_ms && (
                        <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                          <span className="flex items-center space-x-1">
                            <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                            </svg>
                            <span className="font-semibold">
                              {(message.metadata.response_time_ms / 1000).toFixed(2)}s
                            </span>
                          </span>
                          <span className="text-gray-400">
                            {new Date(message.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <div ref={chatEndRef} />
            </>
          )}
        </div>

        {/* Enhanced Input Area with gradient border */}
        <div className="border-t-2 border-gray-200 bg-white p-5 shadow-2xl">
          {isLoading && (
            <div className="flex items-center justify-center mb-4 bg-gradient-to-r from-blue-50 to-indigo-50 py-3 rounded-xl border border-blue-200">
              <LoadingSpinner />
              <span className="ml-3 text-sm font-semibold text-blue-700">Generating response...</span>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="flex space-x-3">
              <div className="flex-1 relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => setShowSuggestions(true)}
                  placeholder="Ask anything about your transcriptions..."
                  className="w-full px-5 py-4 pr-12 border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-base placeholder-gray-400 shadow-sm"
                  disabled={isLoading}
                />
                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
              <button
                type="submit"
                disabled={!query.trim() || isLoading}
                className="px-7 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-lg disabled:shadow-none font-semibold"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </button>
            </div>
            
            {/* Enhanced Tips Section */}
            <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
              <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <span>
                <span className="font-semibold text-gray-600">Pro tip:</span> Mention company name, quarter, or year for targeted results
              </span>
            </div>
          </form>
        </div>
      </div>

      {/* Add custom styles for animations and scrollbar */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        
        .animate-slideInRight {
          animation: slideInRight 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.5s ease-out;
        }
        
        .hover\\:scale-102:hover {
          transform: scale(1.02);
        }
        
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, #3b82f6, #6366f1);
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, #2563eb, #4f46e5);
        }
      `}</style>
    </div>
  );
};

export default QAChatModal;
